import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useState } from 'react';
import FormInput from '../../../components/Form/FormInput';
import RichTextEditor from '../../../components/Form/RichTextEditor';

import { validateURL } from '../../../utils/stringHelper';
import ImageForm from '../common/image/ImageForm';
import { InputTextarea } from 'primereact/inputtextarea';
import PropertyField from '../../../components/Properties/PropertyField';
import { ToggleButton } from 'primereact/togglebutton';

function NewsPanel({
  news,
  loading,
  isOpen,
  onCreate,
  onUpdate,
  onClose,
  scope,
  tagsType,
  typeList
}) {
  const [title, setTitle] = useState('');
  const [hat, setHat] = useState('');
  const [content, setContent] = useState('');
  const [publicationDate, setPublicationDate] = useState(undefined);
  const [type, setType] = useState('');
  const [image, setImage] = useState(undefined);
  const [sourceLabel, setSourceLabel] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [isInternal, setIsInternal] = useState(true);

  useEffect(() => {
    if (news) {
      setTitle(news.title);
      setContent(news.content);
      setPublicationDate(new Date(news.publication_date));
      setType(news.type);
      setImage(news.image);
      setHat(news.hat);
      setTags(news?.tags?.map((t) => t.id));
      setIsInternal(news.external_link_url ? false : true);
      setSourceLabel(news.source_label);
      setSourceUrl(news.source_url);
    } else {
      setTitle('');
      setContent('');
      setPublicationDate(new Date());
      setType('');
      setImage(undefined);
      setSourceLabel('');
      setSourceUrl('');
      setTags([]);
      setIsInternal(true);
      setHat('');
    }
  }, [news]);

  const handleValidate = () => {
    const newsPayload = {
      title,
      content,
      type,
      publication_date: moment(publicationDate).format('YYYY-MM-DD'),
      image: image.id,
      source_label: sourceLabel,
      source_url: sourceUrl,
      tags,
      hat
    };
    news ? onUpdate(newsPayload) : onCreate(newsPayload);
  };

  const formIsValid = () => {
    return title.length > 3 && image && hat.length > 3;
  };

  useEffect(() => {
    if (isInternal) {
      setSourceLabel('');
      setSourceUrl('');
    }
  }, [isInternal]);

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider l'article`}
        loading={loading}
        disabled={!formIsValid()}
        onClick={handleValidate}
        severity="success"
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: isInternal ? '80vw' : '40vw' }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${news ? 'Modifier' : 'Ajouter'} une actualité`}</h3>
      }>
      <div className="sidebar-content-container">
        <div
          className={`grid ${isInternal ? 'grid grid-cols-2' : 'grid grid-cols-1'} gap-5 h-full overflow-y-hidden`}>
          {isInternal && (
            <div className="flex flex-col gap-y-3 ">
              <FormInput
                key="content"
                label={'Contenu'}
                required
                isValid={content.length > 3}
                className="grow">
                <RichTextEditor
                  value={content}
                  onChange={(value) => {
                    setContent(value);
                  }}
                  acceptImage
                  acceptHeaders
                  height={'65vh'}
                />
              </FormInput>
            </div>
          )}
          <div className="form-wrapper overflow-y-scroll h-auto">
            <FormInput
              label={'Titre (utilisé également pour le Titre SEO)'}
              required
              isValid={title.length > 3}>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre de l'article"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>

            <div className="grid grid-cols-2 gap-3">
              {typeList && (
                <FormInput label={"Type d'article"} required isValid={type}>
                  <Dropdown
                    id="type"
                    value={type}
                    onChange={(e) => setType(e.value)}
                    options={typeList}
                    optionLabel="label"
                    placeholder="Veuillez sélectionner le type d'article"
                  />
                </FormInput>
              )}
              <FormInput label={'Date de publication'} required isValid={publicationDate}>
                <Calendar
                  id="publication-date"
                  onChange={(e) => setPublicationDate(e.value)}
                  dateFormat="dd/mm/yy"
                  placeholder="Veuillez selectionner la date de publication"
                  maxDate={new Date()}
                  value={publicationDate}
                  locale="fr"
                />
              </FormInput>
            </div>
            <FormInput label={'Résumé'} required={isInternal} isValid={hat && hat.length > 3}>
              <InputTextarea
                id="hat"
                rows={3}
                autoResize
                value={hat || undefined}
                placeholder="Veuillez saisir le contenu du Chapô"
                onChange={(e) => setHat(e.target.value)}
                maxLength={300}
              />
            </FormInput>
            <FormInput label={'Interne / Externe'}>
              <ToggleButton
                onLabel="Interne"
                offLabel="Externe"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.value)}
                className={'w-1/3'}
              />
            </FormInput>
            {isInternal ? (
              <FormInput label={'Mots clés (référencement'}>
                <PropertyField
                  type={tagsType}
                  selectedPropertyList={tags}
                  setSelectedPropertyList={setTags}
                  scope={scope}
                  isAllowedToCreate
                />
              </FormInput>
            ) : (
              <FormInput
                label={'Source'}
                errorMessage={
                  !validateURL(sourceUrl) && "L'URL doit commencer par http:// ou https://"
                }>
                <div className="flex flex-row gap-x-3">
                  <InputText
                    placeholder="Titre de la source"
                    value={sourceLabel}
                    onChange={(e) => setSourceLabel(e.target.value)}
                    className="grow"
                  />

                  <InputText
                    placeholder="https:// ou http://"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    className="grow"
                    invalid={!validateURL(sourceUrl)}
                  />
                </div>
              </FormInput>
            )}

            <ImageForm
              image={image}
              setImage={setImage}

              deleteImage={() => setImage(undefined)}
            />
          </div>
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default NewsPanel;
